// src/components/admin/training/packages/package-table.tsx
import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useGetPackagesQuery,
  useDeletePackageMutation,
} from "@/services/trainingApi";
import { useGetCoachesQuery } from "@/services/coachApi";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import { TrainingPackage, TrainingType, TrainingPackageStatus } from "@/types/training";
import { PackageModal } from "./package-modal";
import { ConfirmationDialog } from "@/components/ui/common/confirmation-dailog";
import { toast } from "@/components/ui/use-toast";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PlusIcon, Edit2, Trash2, Clock, CheckCircle2, XCircle, MoreVertical, Pencil } from "lucide-react";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableFilters from "@/components/ui/table/data-table-filter";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { formatToPKR, displayValue } from "@/lib/helper";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_key: string;
  sort_order: string;
  search_key?: string;
  status?: string;
  training_type?: string;
  coach_id?: string;
  facility_id?: string;
}

const initialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_key: "id",
  sort_order: "desc",
};

export const PackageTable: React.FC = () => {
  const orgId = useSelector(
    (state: RootState) => state.auth.userInfo?.user?.org_id
  ) || 0;

  // Search & Filter state
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteriaType>(initialValue);
  const [inputValue, setInputValue] = useState("");
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [openFilter, setOpenFilter] = useState(false);

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<TrainingPackage | null>(null);
  const [deletePackageId, setDeletePackageId] = useState<number | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Queries
  const { data: packagesData, isLoading, refetch } = useGetPackagesQuery({
    org_id: Number(orgId),
    search: searchCriteria.search_key || undefined,
    status: searchCriteria.status && searchCriteria.status !== "all" ? searchCriteria.status : undefined,
    training_type: searchCriteria.training_type && searchCriteria.training_type !== "all" ? searchCriteria.training_type : undefined,
    coach_id: searchCriteria.coach_id && searchCriteria.coach_id !== "all" ? Number(searchCriteria.coach_id) : undefined,
    facility_id: searchCriteria.facility_id && searchCriteria.facility_id !== "all" ? Number(searchCriteria.facility_id) : undefined,
    limit: searchCriteria.limit,
    offset: searchCriteria.offset,
  });

  const { data: coachesData } = useGetCoachesQuery(
    { org_id: Number(orgId), query: "is_trainer=true&limit=100&sort_key=id&sort_order=desc" },
    { skip: !orgId }
  );

  const { data: facilitiesData } = useGetFacilitiesQuery(
    { orgId: Number(orgId), query: "status=active&limit=100" },
    { skip: !orgId }
  );

  const [deletePackage, { isLoading: isDeleting }] = useDeletePackageMutation();

  const packagesList = useMemo(() => {
    return Array.isArray(packagesData?.data) ? packagesData.data : [];
  }, [packagesData]);

  const totalRecords = packagesData?.filtered_counts || packagesData?.total_counts || packagesList.length;

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const coachesList = Array.isArray(coachesData?.data) ? coachesData.data : [];
  const facilitiesList = Array.isArray(facilitiesData?.data) ? facilitiesData.data : [];

  const handleEdit = (pkg: TrainingPackage) => {
    setEditingPackage(pkg);
    setOpenModal(true);
  };

  const handleCreateNew = () => {
    setEditingPackage(null);
    setOpenModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletePackageId) return;
    try {
      await deletePackage(deletePackageId).unwrap();
      toast({ title: "Success", description: "Package deleted successfully" });
      setOpenDeleteDialog(false);
      setDeletePackageId(null);
      refetch();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Delete Failed",
        description: err?.data?.message || "Failed to delete package.",
      });
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "select",
      name: "training_type",
      label: "Training Type",
      options: [
        { id: TrainingType.ONE_TO_ONE, name: "1-on-1 Personal" },
        { id: TrainingType.GROUP_PERSONAL, name: "Group Personal" },
        { id: TrainingType.GROUP_TRAINING, name: "Group Training" },
      ],
      function: (value: string) => handleFilterChange("training_type", value),
    },
    {
      type: "select",
      name: "coach_id",
      label: "Trainer / Coach",
      options: coachesList.map((c: any) => ({
        id: String(c.id),
        name: c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || `Coach #${c.id}`,
      })),
      function: (value: string) => handleFilterChange("coach_id", value),
    },
    {
      type: "select",
      name: "facility_id",
      label: "Facility / Studio",
      options: facilitiesList.map((f: any) => ({
        id: String(f.id),
        name: f.name || `Facility #${f.id}`,
      })),
      function: (value: string) => handleFilterChange("facility_id", value),
    },
  ];

  return (
    <div className="w-full space-y-4">
      {/* Top Header & Actions Matching Facilities Pattern */}
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {/* Create New Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="bg-primary text-black flex items-center justify-center h-9 w-9 md:h-8 md:w-auto md:px-3 md:gap-1 p-0 rounded-md"
                  onClick={handleCreateNew}
                >
                  <PlusIcon className="h-4 w-4 shrink-0" />
                  <span className="hidden md:inline text-sm font-semibold">Create Package</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="md:hidden">
                <p>Create Package</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Filter Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`border rounded-md h-9 w-9 md:h-8 md:w-8 text-gray-400 flex items-center justify-center shrink-0 ${
                    Object.keys(filterData).length ? "border-primary" : ""
                  }`}
                  onClick={() => setOpenFilter(true)}
                >
                  <i
                    className={`fa fa-filter ${
                      Object.keys(filterData).length ? "text-primary" : ""
                    }`}
                  ></i>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to apply filter</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Desktop Listing Table */}
      <div className="hidden md:block rounded-none border border-border">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
            <TableHeader className="bg-secondary">
              <TableRow>
                <TableHead className="font-semibold">Package Name</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Trainer</TableHead>
                <TableHead className="font-semibold">Facility</TableHead>
                <TableHead className="font-semibold">Price</TableHead>
                <TableHead className="font-semibold">Credits</TableHead>
                <TableHead className="font-semibold">Duration & Validity</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-28 text-center">
                    <div className="flex space-x-2 justify-center items-center bg-white py-8">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : packagesList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center text-sm text-gray-500">
                    No training packages found.
                  </TableCell>
                </TableRow>
              ) : (
                packagesList.map((pkg) => {
                  const is1on1 = pkg.training_type === TrainingType.ONE_TO_ONE;
                  const isActive = pkg.status === TrainingPackageStatus.ACTIVE;

                  return (
                    <TableRow
                      key={pkg.id}
                      className="cursor-pointer hover:bg-secondary transition-colors"
                      onClick={(e) => {
                        if (
                          e.target instanceof Element &&
                          !e.target.closest("button")
                        ) {
                          handleEdit(pkg);
                        }
                      }}
                    >
                      <TableCell>
                        <div className="font-semibold text-sm">{pkg.name}</div>
                        {pkg.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 max-w-xs">
                            {pkg.description}
                          </p>
                        )}
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={is1on1 ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}
                        >
                          {is1on1 ? "1-on-1 Personal" : "Group Package"}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-sm font-medium">
                        {pkg.coach_name || `Coach #${pkg.coach_id}`}
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {pkg.facility_name || `Facility #${pkg.facility_id}`}
                      </TableCell>

                      <TableCell className="text-sm font-bold text-foreground">
                        {formatToPKR(pkg.price)}
                      </TableCell>

                      <TableCell>
                        <div className="text-sm font-semibold">{pkg.total_credits} Credits</div>
                        <span className="text-[11px] text-muted-foreground">
                          {pkg.credits_per_session || 1} cred / session
                        </span>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-1 text-xs font-medium">
                          <Clock className="size-3 text-muted-foreground" />
                          {Number(pkg.slot_duration_hours) === 1
                            ? "60 min (1.0h)"
                            : `${pkg.slot_duration_hours}h`}{" "}
                          per session
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          Valid for {pkg.validity_days || 90} days
                        </span>
                      </TableCell>

                      <TableCell>
                        {isActive ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                            <CheckCircle2 className="size-3 text-green-600" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-200">
                            <XCircle className="size-3 text-gray-400" /> Inactive
                          </span>
                        )}
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
                              >
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-36" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem
                                onClick={() => handleEdit(pkg)}
                                className="cursor-pointer"
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer text-red-600 focus:text-red-600"
                                onClick={() => {
                                  setDeletePackageId(pkg.id);
                                  setOpenDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center py-10">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : packagesList.length > 0 ? (
          packagesList.map((pkg) => {
            const is1on1 = pkg.training_type === TrainingType.ONE_TO_ONE;
            const isActive = pkg.status === TrainingPackageStatus.ACTIVE;

            return (
              <div
                key={pkg.id}
                className="border border-border rounded-md p-3 space-y-2 bg-white cursor-pointer hover:bg-secondary/40 transition-colors"
                onClick={() => handleEdit(pkg)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{pkg.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {pkg.coach_name || `Coach #${pkg.coach_id}`} • {pkg.facility_name || `Facility #${pkg.facility_id}`}
                    </p>
                  </div>
                  <div
                    className="flex items-center gap-1 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      className={`${isActive ? "bg-green-500" : "bg-gray-400"} rounded-full w-2.5 h-2.5 shrink-0`}
                    ></span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="flex h-7 w-7 p-0 data-[state=open]:bg-muted"
                        >
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-36" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem
                          onClick={() => handleEdit(pkg)}
                          className="cursor-pointer"
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer text-red-600 focus:text-red-600"
                          onClick={() => {
                            setDeletePackageId(pkg.id);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <div className="text-muted-foreground">Type</div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {is1on1 ? "1-on-1" : "Group"}
                    </Badge>
                  </div>

                  <div className="text-muted-foreground">Price</div>
                  <div className="text-right font-bold text-foreground">
                    {formatToPKR(pkg.price)}
                  </div>

                  <div className="text-muted-foreground">Total Credits</div>
                  <div className="text-right font-semibold">{pkg.total_credits}</div>

                  <div className="text-muted-foreground">Session Duration</div>
                  <div className="text-right">{pkg.slot_duration_hours}h</div>

                  <div className="text-muted-foreground">Validity</div>
                  <div className="text-right">{pkg.validity_days || 90} days</div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            No training packages found.
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalRecords > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      {/* Filter Sheet */}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      {/* Package Form Modal */}
      <PackageModal
        open={openModal}
        onOpenChange={setOpenModal}
        selectedPackage={editingPackage}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title="Delete Training Package"
        message="Are you sure you want to delete this training package? It will no longer be available for sale in POS."
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};